import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import {
  Button,
  TextField,
  Typography,
  Container,
  Box,
  Alert,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom'; // استخدام useParams للحصول على horseId
import CountryFlagLabel from '../../components/CountryFlagLabel';
import { formatDateInput } from '../../utils/dateUtils';
const apiUrl = process.env.REACT_APP_API_URL;

// إعداد axios مرة واحدة: baseURL + Authorization
axios.defaults.baseURL = apiUrl;
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const HorseEdit = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate(); // لإعادة التوجيه بعد التعديل
  const { horseId } = useParams(); // الحصول على horseId من عنوان URL
  const [name, setName] = useState('');
  const [nameArabic, setNameArabic] = useState('');
  const [fatherName, setFatherName] = useState('');
  const [fatherNameArabic, setFatherNameArabic] = useState('');
  const [motherName, setMotherName] = useState('');
  const [motherNameArabic, setMotherNameArabic] = useState('');
  const [breederName, setBreederName] = useState('');
  const [breederNameArabic, setBreederNameArabic] = useState('');
  const [breedId, setBreedId] = useState('');
  const [rfidChipNumber, setRfidChipNumber] = useState('');
  const [colorId, setColorId] = useState('');
  const [gender, setGender] = useState('');
  const [dateOfBirth, setDateOfBirth] = useState('');
  const [ownerId, setOwnerId] = useState('');
  const [countryOfOriginId, setCountryOfOriginId] = useState('');
  const [owners, setOwners] = useState([]);
  const [colors, setColors] = useState([]);
  const [breeds, setBreeds] = useState([]);
  const [countries, setCountries] = useState([]);
  const [registrationNumber, setRegistrationNumber] = useState('');
  const [registrationDate, setRegistrationDate] = useState('');
  const [pedigreeHtml, setPedigreeHtml] = useState('');
  const [image, setImage] = useState(null);
  const [registrationCertificate, setRegistrationCertificate] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [horse, setHorse] = useState(null); // لحفظ بيانات الخيل المحملة
  //const deleteImage = req.body.delete_image === 'true';
  // const deleteRegistrationCertificate =  req.body.delete_registration_certificate === 'true';

  useEffect(() => {
    if (horseId) {
      const fetchHorseData = async () => {
        try {
          const response = await axios.get(`${apiUrl}/horses/${horseId}`);
          const horseData = response.data;

          setHorse(horseData);
          setName(horseData.name ?? '');
          setNameArabic(horseData.name_arabic ?? '');
          setFatherName(horseData.father_name ?? '');
          setFatherNameArabic(horseData.father_name_arabic ?? '');
          setMotherName(horseData.mother_name ?? '');
          setMotherNameArabic(horseData.mother_name_arabic ?? '');
          setBreederName(horseData.breeder_name ?? '');
          setBreederNameArabic(horseData.breeder_name_arabic ?? '');
          setBreedId(horseData.breed_id ?? '');
          setRfidChipNumber(horseData.rfid_chip_number ?? '');
          setColorId(horseData.color_id ?? '');
          setGender(horseData.gender ?? '');
          setDateOfBirth(
            horseData.date_of_birth ? formatDateInput(horseData.date_of_birth) : ''
          );
          setOwnerId(horseData.owner_id ?? '');
          setCountryOfOriginId(horseData.country_of_origin_id ?? '');
          setRegistrationNumber(horseData.registration_number ?? '');
          setRegistrationDate(
            horseData.registration_date
              ? formatDateInput(horseData.registration_date)
              : ''
          );
          setPedigreeHtml(horseData.pedigree_html ?? '');

          // عرض الملفات
          if (horseData.image) {
            setImage(horseData.image);
          }
          if (horseData.registration_certificate) {
            setRegistrationCertificate(horseData.registration_certificate);
          }
        } catch (error) {
          setError(t('error_loading_horse_data'));
        }
      };

      fetchHorseData();
    }
  }, [horseId, t]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const ownersResponse = await axios.get(`${apiUrl}/owners/summary`);
        setOwners(ownersResponse.data);

        const colorsResponse = await axios.get(`${apiUrl}/horse_colors`);
        setColors(colorsResponse.data);

        const breedsResponse = await axios.get(`${apiUrl}/horse_breeds`);
        setBreeds(breedsResponse.data);

        const countriesResponse = await axios.get(`${apiUrl}/countries`);
        setCountries(countriesResponse.data);
      } catch (err) {
        console.error('Failed to fetch data: ' + err.message);
      }
    };

    fetchData();
  }, []);

  const validateFields = () => {
    if (!name) {
      setError(t('please_fill_horse_name'));
      return false;
    }
    if (!nameArabic) {
      setError(t('please_fill_horse_name_arabic'));
      return false;
    }
    if (!gender) {
      setError(t('please_select_gender'));
      return false;
    }
    if (!dateOfBirth) {
      setError(t('please_select_date_of_birth'));
      return false;
    }
    /*if (!ownerId) {
      setError(t('please_select_owner'));
      return false;
    }
    if (!registrationNumber) {
      setError(t('please_fill_registration_number'));
      return false;
    }
    if (!registrationDate) {
      setError(t('please_select_registration_date'));
      return false;
    }
    if (!colorId) {
      setError(t('please_select_color'));
      return false;
    }
    if (!breedId) {
      setError(t('please_select_breed'));
      return false;
    }
    if (!countryOfOriginId) {
      setError(t('please_select_country_of_origin'));
      return false;
    }*/
    // شهادة التسجيل مطلوبة كما في صفحة التسجيل:
    const hasCert =
      Boolean(registrationCertificate) ||
      Boolean(horse?.registration_certificate);
    if (!hasCert) {
      setError(t('please_upload_registration_certificate'));
      return false;
    }
    return true;
  };
  const cleanText = (v) =>
    v === undefined || v === null || v === 'null' ? '' : v;
  const appendIntIfPresent = (fd, key, v) => {
    if (v !== '' && v !== undefined && v !== null && v !== 'null') {
      fd.append(key, String(v));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateFields()) return;

    const formData = new FormData();
    formData.append('name', cleanText(name));
    formData.append('name_arabic', cleanText(nameArabic));
    formData.append('father_name', cleanText(fatherName));
    formData.append('father_name_arabic', cleanText(fatherNameArabic));
    formData.append('mother_name', cleanText(motherName));
    formData.append('mother_name_arabic', cleanText(motherNameArabic));
    formData.append('breeder_name', cleanText(breederName));
    formData.append('breeder_name_arabic', cleanText(breederNameArabic));

    appendIntIfPresent(formData, 'breed_id', breedId);
    appendIntIfPresent(formData, 'color_id', colorId);
    appendIntIfPresent(formData, 'owner_id', ownerId);
    appendIntIfPresent(formData, 'country_of_origin_id', countryOfOriginId);

    formData.append('rfid_chip_number', cleanText(rfidChipNumber));
    formData.append('gender', gender);
    formData.append('date_of_birth', cleanText(dateOfBirth));
    formData.append('registration_number', cleanText(registrationNumber));
    formData.append('registration_date', cleanText(registrationDate));
    formData.append('pedigree_html', cleanText(pedigreeHtml));

    if (image instanceof File) {
      formData.append('image', image);
    } else if (image === null && horse?.image) {
      formData.append('delete_image', 'true');
    }

    if (registrationCertificate instanceof File) {
      formData.append('registration_certificate', registrationCertificate);
    } else if (
      registrationCertificate === null &&
      horse &&
      horse.registration_certificate
    ) {
      formData.append('delete_registration_certificate', 'true');
    }

    // لو عندك زر حذف صريح، أرسل فلاغ الحذف فقط عند الضغط:
    // if (deleteRegCert) formData.append('delete_registration_certificate', 'true');

    try {
      const userId = localStorage.getItem('userId'); // ✅ جلب رقم المستخدم من localStorage
      formData.append('modified_by', userId); // ✅ إرساله مع البيانات

      await axios.put(`${apiUrl}/horses/${horseId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      setSuccess(t('horse_updated_successfully'));
      setError('');
      navigate('/horse-list');
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        t('error_updating_horse');
      setError(msg);
      setSuccess('');
    }
  };

  return (
    <Container component="main" maxWidth="xs">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
        dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}
      >
        <Typography component="h1" variant="h5">
          {t('edit_horse')}
        </Typography>
        <Box component="form" onSubmit={handleSubmit} noValidate sx={{ mt: 1 }}>
          <TextField
            margin="normal"
            required
            fullWidth
            id="name"
            label={t('horse_name')}
            name="name"
            autoComplete="name"
            autoFocus
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <TextField
            margin="normal"
            required
            fullWidth
            id="nameArabic"
            label={t('horse_name_arabic')}
            name="nameArabic"
            autoComplete="nameArabic"
            value={nameArabic}
            onChange={(e) => setNameArabic(e.target.value)}
          />
          <TextField
            margin="normal"
            fullWidth
            id="fatherName"
            label={t('father_name')}
            name="fatherName"
            value={fatherName}
            onChange={(e) => setFatherName(e.target.value)}
          />
          <TextField
            margin="normal"
            fullWidth
            id="fatherNameArabic"
            label={t('father_name_arabic')}
            name="fatherNameArabic"
            value={fatherNameArabic}
            onChange={(e) => setFatherNameArabic(e.target.value)}
          />
          <TextField
            margin="normal"
            fullWidth
            id="motherName"
            label={t('mother_name')}
            name="motherName"
            value={motherName}
            onChange={(e) => setMotherName(e.target.value)}
          />
          <TextField
            margin="normal"
            fullWidth
            id="motherNameArabic"
            label={t('mother_name_arabic')}
            name="motherNameArabic"
            value={motherNameArabic}
            onChange={(e) => setMotherNameArabic(e.target.value)}
          />
          <TextField
            margin="normal"
            fullWidth
            id="breederName"
            label={t('breeder_name')}
            name="breederName"
            value={breederName}
            onChange={(e) => setBreederName(e.target.value)}
          />
          <TextField
            margin="normal"
            fullWidth
            id="breederNameArabic"
            label={t('breeder_name_arabic')}
            name="breederNameArabic"
            value={breederNameArabic}
            onChange={(e) => setBreederNameArabic(e.target.value)}
          />
          <FormControl fullWidth margin="normal" required>
            <InputLabel id="breedId-label">{t('breed')}</InputLabel>
            <Select
              labelId="breedId-label"
              id="breedId"
              value={
                breeds.find((breed) => breed.breed_id === breedId)
                  ? breedId
                  : ''
              } // التحقق من وجود القيمة
              label={t('breed')}
              onChange={(e) =>
                setBreedId(e.target.value === '' ? '' : Number(e.target.value))
              }
            >
              {breeds.length > 0 ? ( // التأكد من تحميل البيانات
                breeds.map((breed) => (
                  <MenuItem key={breed.breed_id} value={breed.breed_id}>
                    {breed.breed_english} / {breed.breed_arabic}
                  </MenuItem>
                ))
              ) : (
                <MenuItem value="">{t('no_breeds_available')}</MenuItem> // عرض رسالة في حال عدم وجود بيانات
              )}
            </Select>
          </FormControl>

          <TextField
            margin="normal"
            fullWidth
            id="rfidChipNumber"
            label={t('rfid_chip_number')}
            name="rfidChipNumber"
            autoComplete="rfidChipNumber"
            value={rfidChipNumber}
            onChange={(e) => setRfidChipNumber(e.target.value)}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel id="colorId-label">{t('color_id')}</InputLabel>
            <Select
              labelId="colorId-label"
              id="colorId"
              //value={colorId}
              value={colors.find((c) => c.color_id === colorId) ? colorId : ''}
              label={t('color_id')}
              onChange={(e) =>
                setColorId(e.target.value === '' ? '' : Number(e.target.value))
              }
            >
              {colors.map((color) => (
                <MenuItem key={color.color_id} value={color.color_id}>
                  {color.color_english} / {color.color_arabic}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel id="gender-label">{t('gender')}</InputLabel>
            <Select
              labelId="gender-label"
              id="gender"
              value={gender}
              label={t('gender')}
              onChange={(e) => setGender(e.target.value)}
            >
              <MenuItem value="S">{t('STALLION')} / حصان</MenuItem>
              <MenuItem value="M">{t('MARE')} / فرس</MenuItem>
            </Select>
          </FormControl>
          <TextField
            margin="normal"
            required
            fullWidth
            id="dateOfBirth"
            label={t('date_of_birth')}
            name="dateOfBirth"
            type="date"
            InputLabelProps={{
              shrink: true,
            }}
            value={dateOfBirth}
            onChange={(e) => setDateOfBirth(e.target.value)}
          />
          <FormControl fullWidth margin="normal">
            <InputLabel id="ownerId-label">{t('owner_id')}</InputLabel>
            <Select
              labelId="ownerId-label"
              id="ownerId"
              //value={ownerId}
              value={owners.find((o) => o.owner_id === ownerId) ? ownerId : ''}
              label={t('owner_id')}
              onChange={(e) =>
                setOwnerId(e.target.value === '' ? '' : Number(e.target.value))
              }
            >
              {owners.map((owner) => (
                <MenuItem key={owner.owner_id} value={owner.owner_id}>
                  {owner.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl fullWidth margin="normal">
            <InputLabel id="countryOfOriginId-label">
              {t('country_of_origin')}
            </InputLabel>
            <Select
              labelId="countryOfOriginId-label"
              id="countryOfOriginId"
              //value={countryOfOriginId}
              value={
                countries.find((c) => c.country_id === countryOfOriginId)
                  ? countryOfOriginId
                  : ''
              }
              label={t('country_of_origin')}
              onChange={(e) =>
                setCountryOfOriginId(
                  e.target.value === '' ? '' : Number(e.target.value)
                )
              }
            >
              {countries.map((country) => (
                <MenuItem key={country.country_id} value={country.country_id}>
                  <CountryFlagLabel
                    country={
                      i18n.language === 'ar'
                        ? country.country_arabic
                        : country.country_english
                    }
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            margin="normal"
            fullWidth
            id="registrationNumber"
            label={t('registration_number')}
            name="registrationNumber"
            autoComplete="registrationNumber"
            value={registrationNumber}
            onChange={(e) => setRegistrationNumber(e.target.value)}
          />
          <TextField
            margin="normal"
            fullWidth
            id="registrationDate"
            label={t('registration_date')}
            name="registrationDate"
            type="date"
            InputLabelProps={{
              shrink: true,
            }}
            value={registrationDate}
            onChange={(e) => setRegistrationDate(e.target.value)}
          />
          <TextField
            margin="normal"
            fullWidth
            id="pedigreeHtml"
            label={t('pedigree_html')}
            name="pedigreeHtml"
            multiline
            rows={4}
            value={pedigreeHtml}
            onChange={(e) => setPedigreeHtml(e.target.value)}
          />
          <input
            type="file"
            accept="image/*"
            onChange={(e) => setImage(e.target.files[0])}
            style={{ marginTop: '16px' }}
          />
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            {t('upload_horse_image')}
          </Typography>
          <input
            type="file"
            accept="application/pdf"
            onChange={(e) => setRegistrationCertificate(e.target.files[0])}
            style={{ marginTop: '16px' }}
          />
          <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
            {t('upload_registration_certificate')} *
          </Typography>
          {horse && horse.image && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="textSecondary">
                {t('current_horse_image')}
              </Typography>
              <img
                src={`${horse.image}`}
                alt="Horse"
                style={{
                  width: '150px', // تحديد العرض
                  height: 'auto', // الارتفاع تلقائي للحفاظ على نسبة الصورة
                  marginTop: '10px',
                  borderRadius: '8px',
                  objectFit: 'cover', // لضمان قص الصورة بشكل أنيق عند الحاجة
                }}
              />
              {/*
              <Box sx={{ textAlign: 'center', mt: 1 }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => {
                    setImage(null); // حذف الصورة الجديدة إن وُجدت
                    setHorse((prevHorse) => ({
                      ...prevHorse,
                      image: null, // حذف الصورة القديمة من الواجهة
                    }));
                  }}
                  sx={{
                    fontSize: '12px',
                    padding: '5px 10px',
                    minWidth: '100px',
                  }}
                >
                  {t('delete_image')}
                </Button>
              </Box>
            </Box>
          )}

          {horse && horse.registration_certificate && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Typography variant="body2" color="textSecondary">
                {t('current_registration_certificate')}
              </Typography>
              <a
                href={horse.registration_certificate}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  textDecoration: 'none',
                  color: '#007bff',
                  display: 'block',
                  marginBottom: '10px',
                }}
              >
                {t('view_registration_certificate')}
              </a>
              <Button
                variant="outlined"
                color="secondary"
                onClick={() => setRegistrationCertificate(null)} // حذف شهادة التسجيل الحالية
                sx={{
                  fontSize: '12px',
                  padding: '5px 10px',
                  minWidth: '100px',
                }}
              >
                {t('delete_certificate')}
              </Button>

*/}
            </Box>
          )}

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between', // توزيع الأزرار بالتساوي
              gap: 2, // إضافة مسافة بين الأزرار
              mt: 3, // إضافة مسافة في الأعلى
              mb: 2, // إضافة مسافة في الأسفل
            }}
          >
            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ flex: 1 }} // جعل الزر يمتد ليأخذ نفس حجم الزر الآخر
            >
              {t('edit_horse')}{' '}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={() => navigate('/horse-list')}
              sx={{ flex: 1 }} // جعل الزر يمتد ليأخذ نفس حجم الزر الآخر
            >
              {t('Cancel')}
            </Button>
          </Box>

          {error && <Alert severity="error">{error}</Alert>}
          {success && <Alert severity="success">{success}</Alert>}
        </Box>
      </Box>
    </Container>
  );
};

export default HorseEdit;
